import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { ResponsiveLayout } from '@/components/ResponsiveLayout';
import { SectionHeader } from '@/components/SectionHeader';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  BookOpen, Copy, Send, CheckCircle2, AlertCircle, Loader2,
  Image, Video, ArrowRight, Sparkles, MessageSquare,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STYLE_META = {
  strict: {
    title: 'Strict Format (Recommended for new providers)',
    badge: 'Official',
    badgeClass: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
    description: 'Numbered list with dashes. Easiest to parse, lowest error rate.',
  },
  ftc_style: {
    title: 'FTC Style — emoji + pair + sign',
    badge: 'Common',
    badgeClass: 'bg-blue-500/15 text-blue-300 border-blue-500/30',
    description: '✅ Pair Position +Pips pips (Channel). Each line has all info inline.',
  },
  ref_number_style: {
    title: 'Reference Number Style — R1/R2/R3',
    badge: 'Power user',
    badgeClass: 'bg-purple-500/15 text-purple-300 border-purple-500/30',
    description: 'R{N}: prefix to label each trade. Supports Reversal as zero-pip loss.',
  },
  simple_style: {
    title: 'Simple Style — no pair, no date',
    badge: 'Minimalist',
    badgeClass: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
    description: 'Just Position - Pips ✅. Pair defaults to XAUUSD, date uses Telegram timestamp.',
  },
};

export default function TelegramTemplateGuidePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testText, setTestText] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  const token = localStorage.getItem('token');
  const h = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  useEffect(() => {
    axios.get(`${API}/operations/template/sample`, h).then((r) => {
      setData(r.data);
      setTestText(r.data?.samples?.strict || r.data?.sample || '');
    }).catch(() => {}).finally(() => setLoading(false));
  }, [h]);

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const runTest = async () => {
    if (!testText.trim()) return;
    setTesting(true);
    try {
      const r = await axios.post(`${API}/operations/template/parse`, { text: testText }, h);
      setTestResult(r.data);
    } catch (e) {
      toast.error('Parse failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <ResponsiveLayout>
        <div className="flex-1 flex items-center justify-center min-h-screen bg-[hsl(var(--background))]">
          <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
        </div>
      </ResponsiveLayout>
    );
  }

  return (
    <ResponsiveLayout>
      <div className="flex-1 min-h-screen bg-[hsl(var(--background))] text-[hsl(var(--text-primary))] p-4 lg:p-6">
        <SectionHeader
          icon={BookOpen}
          title="Telegram Template Guide"
          subtitle="Standard daily performance templates supported by CRM auto-sync"
          iconBg="bg-gradient-to-br from-amber-500 to-orange-600 shadow-amber-500/20"
        />

        {/* Intro */}
        <div className="bg-gradient-to-br from-amber-500/5 to-orange-500/5 border border-amber-500/20 rounded-xl p-5 mb-6" data-testid="guide-intro">
          <div className="flex items-start gap-3">
            <Sparkles className="text-amber-400 flex-shrink-0" size={20} />
            <div className="space-y-2 text-sm">
              <p>
                <strong className="text-amber-300">Cara kerja:</strong> Sila post daily performance summary di Telegram channel masing-masing. CRM akan auto-detect & extract trade data — tak perlu copy-paste mana-mana.
              </p>
              <p className="text-xs text-[hsl(var(--text-muted))]">
                Sistem support 4 style berbeza di bawah. Pilih mana yang sesuai dengan branding channel kau — semua akan capture data sama.
                Editor boleh edit post & data akan auto-update.
              </p>
            </div>
          </div>
        </div>

        {/* Style Templates */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          {Object.entries(data?.samples || {}).map(([key, sample]) => {
            const meta = STYLE_META[key];
            return (
              <div key={key} className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl p-4" data-testid={`template-${key}`}>
                <div className="flex items-center justify-between mb-2 gap-2">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-sm font-semibold">{meta?.title || key}</h3>
                      {meta?.badge && <Badge className={`text-[9px] border ${meta.badgeClass}`}>{meta.badge}</Badge>}
                    </div>
                    <p className="text-[11px] text-[hsl(var(--text-muted))]">{meta?.description}</p>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => copy(sample)} className="h-7 text-[10px] text-amber-400 hover:bg-amber-500/10" data-testid={`copy-${key}`}>
                    <Copy size={10} className="mr-1" /> Copy
                  </Button>
                </div>
                <pre className="text-[11px] bg-[hsl(var(--background))] border border-[hsl(var(--border))] rounded p-3 text-emerald-300 font-mono whitespace-pre-wrap overflow-x-auto">
{sample}
                </pre>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-amber-500/40 text-amber-300 mt-2 w-full hover:bg-amber-500/10"
                  onClick={() => { setTestText(sample); document.getElementById('test-section')?.scrollIntoView({ behavior: 'smooth' }); }}
                  data-testid={`try-${key}`}
                >
                  <ArrowRight size={11} className="mr-1.5" /> Try this style below
                </Button>
              </div>
            );
          })}
        </div>

        {/* Flexibility Notes */}
        <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl p-4 mb-6">
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2"><Sparkles size={14} className="text-cyan-400" /> Cool Things The Parser Handles</h3>
          <ul className="space-y-1.5 text-xs text-[hsl(var(--text-secondary))]">
            {(data?.flexibility_notes || []).map((note, i) => (
              <li key={i} className="flex items-start gap-2">
                <CheckCircle2 size={12} className="text-emerald-400 flex-shrink-0 mt-0.5" /> {note}
              </li>
            ))}
          </ul>
        </div>

        {/* Aliases */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl p-4">
            <h4 className="text-sm font-semibold mb-2 flex items-center gap-2"><Image size={12} className="text-pink-400" /> Pair Shortcuts</h4>
            <div className="flex flex-wrap gap-1">
              {Object.entries(data?.pair_aliases || {}).slice(0, 20).map(([canon, aliases]) => (
                <Badge key={canon} className="text-[9px] bg-[hsl(var(--surface))] text-[hsl(var(--text-muted))] border border-[hsl(var(--border))]" title={`${aliases.join(', ')} → ${canon}`}>
                  {aliases[0]} → {canon}
                </Badge>
              ))}
            </div>
          </div>
          <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl p-4">
            <h4 className="text-sm font-semibold mb-2 flex items-center gap-2"><Video size={12} className="text-purple-400" /> Status Codes</h4>
            <div className="space-y-1 text-[11px] text-[hsl(var(--text-secondary))]">
              {Object.entries(data?.status_codes || {}).map(([k, v]) => (
                <div key={k} className="flex items-center gap-2"><code className="text-amber-300">{k}</code> <ArrowRight size={9} /> <span className="font-semibold">{v}</span></div>
              ))}
            </div>
          </div>
          <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl p-4">
            <h4 className="text-sm font-semibold mb-2 flex items-center gap-2"><MessageSquare size={12} className="text-cyan-400" /> Channel Markers</h4>
            <div className="space-y-1 text-[11px] text-[hsl(var(--text-secondary))]">
              {Object.entries(data?.channel_markers || {}).map(([k, v]) => (
                <div key={k} className="leading-tight"><code className="text-cyan-300">{k}</code><br /><span className="opacity-70">{v}</span></div>
              ))}
            </div>
          </div>
        </div>

        {/* Test Area */}
        <div id="test-section" className="bg-gradient-to-br from-blue-500/5 to-cyan-500/5 border border-blue-500/20 rounded-xl p-5" data-testid="test-section">
          <div className="flex items-center gap-2 mb-3">
            <Send size={16} className="text-cyan-400" />
            <h3 className="text-sm font-semibold">Try It — Test Your Format</h3>
          </div>
          <p className="text-xs text-[hsl(var(--text-muted))] mb-3">
            Paste any signal summary text below to see what the parser will extract.
            This is what would happen if you posted this in Telegram.
          </p>
          <Textarea
            value={testText}
            onChange={(e) => setTestText(e.target.value)}
            placeholder="Paste your signal summary here..."
            className="min-h-[160px] bg-[hsl(var(--surface))] border-[hsl(var(--border))] font-mono text-xs"
            data-testid="test-textarea"
          />
          <div className="flex items-center justify-end gap-2 mt-2">
            <Button size="sm" onClick={runTest} disabled={testing || !testText.trim()} className="bg-cyan-500 hover:bg-cyan-600 text-white" data-testid="test-parse-btn">
              {testing ? <Loader2 size={12} className="mr-1.5 animate-spin" /> : <Send size={12} className="mr-1.5" />}
              Parse & Preview
            </Button>
          </div>

          {testResult && (
            <div className="mt-4 space-y-3" data-testid="test-result">
              {/* Header */}
              <div className="border border-[hsl(var(--border))] rounded p-2.5 text-xs">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[hsl(var(--text-muted))]">Parsed header</span>
                  <Badge className={`text-[9px] border ${testResult.trades?.length > 0 ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' : 'bg-red-500/15 text-red-300 border-red-500/30'}`} data-testid="test-result-status">
                    {testResult.trades?.length > 0 ? 'WOULD CAPTURE' : 'NOT DETECTED'}
                  </Badge>
                </div>
                <div className="grid grid-cols-2 gap-2 text-[10px]">
                  <div><span className="text-[hsl(var(--text-muted))]">Channel name:</span> <strong>{testResult.header?.channel_name || '—'}</strong></div>
                  <div><span className="text-[hsl(var(--text-muted))]">Date:</span> <strong>{testResult.header?.date || '—'}</strong> <span className="opacity-50">({testResult.header?.date_source})</span></div>
                </div>
              </div>

              {/* Errors / warnings */}
              {(testResult.errors?.length > 0 || testResult.warnings?.length > 0) && (
                <div className="space-y-1">
                  {testResult.errors?.map((e, i) => (
                    <div key={`e-${i}`} className="bg-red-500/10 border border-red-500/20 rounded p-2 text-[11px] text-red-300 flex items-start gap-1.5">
                      <AlertCircle size={11} className="mt-0.5 flex-shrink-0" /> {e}
                    </div>
                  ))}
                  {testResult.warnings?.map((w, i) => (
                    <div key={`w-${i}`} className="bg-amber-500/10 border border-amber-500/20 rounded p-2 text-[11px] text-amber-300 flex items-start gap-1.5">
                      <AlertCircle size={11} className="mt-0.5 flex-shrink-0" /> {w}
                    </div>
                  ))}
                </div>
              )}

              {/* Trades */}
              {testResult.trades?.length > 0 && (
                <div className="border border-[hsl(var(--border))] rounded overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-[hsl(var(--surface-elevated))]">
                      <tr>
                        <th className="text-left px-2 py-1.5">Pair</th>
                        <th className="text-left px-2 py-1.5">Position</th>
                        <th className="text-left px-2 py-1.5">Status</th>
                        <th className="text-right px-2 py-1.5">Pips</th>
                        <th className="text-left px-2 py-1.5">Save to</th>
                      </tr>
                    </thead>
                    <tbody>
                      {testResult.trades.map((t, i) => (
                        <tr key={i} className="border-t border-[hsl(var(--border))]" data-testid={`test-trade-${i}`}>
                          <td className="px-2 py-1.5 font-semibold text-pink-300">{t.pair}</td>
                          <td className="px-2 py-1.5">{t.position}</td>
                          <td className="px-2 py-1.5">
                            <Badge className={`text-[9px] border ${t.status === 'WIN' ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' : t.status === 'LOSS' ? 'bg-red-500/15 text-red-300 border-red-500/30' : 'bg-gray-500/15 text-gray-300 border-gray-500/30'}`}>{t.status}</Badge>
                          </td>
                          <td className={`px-2 py-1.5 text-right font-semibold ${t.pips > 0 ? 'text-emerald-400' : t.pips < 0 ? 'text-red-400' : ''}`}>{t.pips}</td>
                          <td className="px-2 py-1.5">
                            <Badge className="text-[9px] bg-[hsl(var(--surface-elevated))] border-0">{t.post_to || 'both'}</Badge>
                            {t.channel_marker && <span className="text-[9px] text-[hsl(var(--text-muted))] ml-1">({t.channel_marker})</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    {testResult.total?.stated != null && (
                      <tfoot className="bg-[hsl(var(--surface-elevated))]">
                        <tr>
                          <td colSpan={3} className="px-2 py-1.5 text-right text-[hsl(var(--text-muted))]">Stated total</td>
                          <td colSpan={2} className={`px-2 py-1.5 ${testResult.total.matches ? 'text-emerald-400' : 'text-amber-400'}`}>
                            {testResult.total.stated} {testResult.total.matches ? '✓' : '⚠'}
                          </td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </ResponsiveLayout>
  );
}
